<template>
  <b-sidebar
    id="sidebar-event-sheet"
    sidebar-class="sidebar-lg"
    :visible="value"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @change="$emit('input', $event)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="sheet-header">
        <h5 class="mb-0">
          {{ selectedEvent ? 'Editar' : 'Novo' }} Agendamento
        </h5>
        <div class="d-flex align-items-center">
          <feather-icon
            v-if="selectedEvent"
            icon="TrashIcon"
            class="cursor-pointer text-muted hover-danger"
            @click="$emit('deleteEvent'); hide()"
          />
          <feather-icon
            class="ml-1 cursor-pointer text-muted"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
      </div>

      <!-- Form -->
      <b-form
        class="p-3"
        @submit.prevent="$emit('saveEvent')"
      >
        <!-- Cliente -->
        <b-form-group
          label="Cliente *"
          label-for="cliente-search"
        >
          <div class="position-relative">
            <b-form-input
              id="cliente-search"
              :value="clienteSearch"
              type="text"
              placeholder="Digite nome, CPF/CNPJ ou ID do cliente"
              autocomplete="off"
              :disabled="!!selectedEvent"
              @input="$emit('update:clienteSearch', $event)"
              @focus="$emit('clienteSearchFocus')"
              @blur="$emit('clienteSearchBlur')"
              @keydown.down.prevent="$emit('navigateClienteDown')"
              @keydown.up.prevent="$emit('navigateClienteUp')"
              @keydown.enter.prevent="$emit('selectHighlightedCliente')"
            />
            <small
              v-if="!selectedEvent && clienteSearch.length > 0 && clienteSearch.length < 3"
              class="text-muted"
            >
              Digite ao menos 3 caracteres para buscar.
            </small>

            <!-- Suggestions dropdown -->
            <div
              v-if="showClienteSearchDropdown"
              class="cliente-dropdown"
            >
              <template v-if="showClienteSuggestions">
                <div
                  v-for="(cliente, index) in clienteSuggestions"
                  :key="cliente.id"
                  class="cliente-item"
                  :class="{ highlighted: highlightedClienteIndex === index }"
                  @mousedown.prevent="$emit('selectCliente', cliente)"
                >
                  <span class="cliente-nome">{{ cliente.nome }}</span>
                  <span class="cliente-id">
                    ID {{ cliente.id }}<template v-if="cliente.cpf_cnpj"> - {{ cliente.cpf_cnpj }}</template>
                  </span>
                </div>
              </template>
              <div
                v-else
                class="cliente-empty"
              >
                Nenhum cliente encontrado.
              </div>
            </div>
          </div>
        </b-form-group>

        <!-- Data de Agendamento -->
        <b-form-group
          label="Data e Hora *"
          label-for="data-agendamento"
        >
          <flat-pickr
            id="data-agendamento"
            :value="eventForm.data_agendamento"
            class="form-control"
            :config="{ enableTime: true, dateFormat: 'd/m/Y H:i', time_24hr: true, locale: 'pt' }"
            placeholder="Selecione a data e hora (DD/MM/YYYY HH:mm)"
            @input="eventForm.data_agendamento = $event"
          />
        </b-form-group>

        <!-- Executivo -->
        <b-form-group
          v-if="isNotExecutivoCobranca"
          label="Executivo"
          label-for="executivo"
        >
          <b-form-select
            id="executivo"
            :value="eventForm.atendente_id"
            :options="executivoSelectOptions"
            @change="eventForm.atendente_id = $event"
          />
        </b-form-group>

        <!-- Observações -->
        <b-form-group
          label="Observações"
          label-for="observacoes"
        >
          <b-form-textarea
            id="observacoes"
            v-model="eventForm.observacoes"
            placeholder="Observações do agendamento..."
            rows="3"
          />
        </b-form-group>

        <!-- Status -->
        <b-form-group
          label="Status"
          label-for="status"
        >
          <b-form-select
            id="status"
            v-model="eventForm.status"
            :options="statusSelectOptions"
          />
        </b-form-group>

        <!-- Motivo -->
        <b-form-group
          label="Motivo"
          label-for="motivo"
        >
          <b-form-select
            id="motivo"
            v-model="eventForm.motivo"
            :options="motivoSelectOptions"
          />
        </b-form-group>

        <!-- Submit -->
        <b-button
          type="submit"
          variant="primary"
          block
          :disabled="saving"
          class="mt-2"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-1"
          />
          {{ selectedEvent ? 'Reagendar' : 'Criar Agendamento' }}
        </b-button>

        <!-- Status Update (edit mode) -->
        <div
          v-if="selectedEvent"
          class="mt-3 pt-3 border-top"
        >
          <h6 class="text-primary d-flex align-items-center mb-2">
            <feather-icon
              icon="ActivityIcon"
              size="16"
              class="mr-1"
            />
            Atualizar Status
          </h6>

          <b-form-group
            label="Status"
            label-for="status-select"
          >
            <b-form-select
              id="status-select"
              v-model="statusForm.status"
              :options="statusSelectOptions"
            />
          </b-form-group>

          <b-form-group
            label="Motivo"
            label-for="status-motivo"
          >
            <b-form-select
              id="status-motivo"
              v-model="statusForm.motivo"
              :options="motivoSelectOptions"
            />
          </b-form-group>

          <b-form-group
            label="Observações"
            label-for="status-obs"
          >
            <b-form-textarea
              id="status-obs"
              v-model="statusForm.observacoes"
              placeholder="Observações..."
              rows="2"
            />
          </b-form-group>

          <b-button
            variant="outline-success"
            block
            size="sm"
            :disabled="updatingStatus"
            @click="$emit('updateStatus')"
          >
            <b-spinner
              v-if="updatingStatus"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="CheckIcon"
              size="14"
              class="mr-1"
            />
            {{ updatingStatus ? 'Atualizando...' : 'Atualizar Status' }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormTextarea,
  BFormSelect, BButton, BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import { statusOptions, motivoOptions } from '../calendar-utils'

export default {
  name: 'EventSheet',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BButton,
    BSpinner,
    flatPickr,
  },
  props: {
    value: { type: Boolean, default: false },
    selectedEvent: { type: Object, default: null },
    eventForm: { type: Object, required: true },
    statusForm: { type: Object, required: true },
    clienteSearch: { type: String, default: '' },
    clienteSuggestions: { type: Array, default: () => [] },
    highlightedClienteIndex: { type: Number, default: -1 },
    isSearchingClientes: { type: Boolean, default: false },
    showClienteSearchDropdown: { type: Boolean, default: false },
    showClienteSuggestions: { type: Boolean, default: false },
    isNotExecutivoCobranca: { type: Boolean, default: true },
    executivoOptionsForForm: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    updatingStatus: { type: Boolean, default: false },
  },
  computed: {
    statusSelectOptions() {
      return [
        { value: '', text: 'Selecione o status', disabled: true },
        ...statusOptions.map(s => ({ value: s.value, text: s.label })),
      ]
    },
    motivoSelectOptions() {
      return [
        { value: '', text: 'Selecione o motivo (opcional)' },
        ...motivoOptions.map(m => ({ value: m.value, text: m.label })),
      ]
    },
    executivoSelectOptions() {
      return [
        { value: undefined, text: 'Selecione o executivo', disabled: true },
        ...this.executivoOptionsForForm.map(e => ({ value: e.value, text: e.label })),
      ]
    },
  },
}
</script>

<style scoped>
.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #dadce0;
}

.hover-danger:hover {
  color: #dc3545 !important;
}

.cliente-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 1050;
  margin-top: 4px;
  max-height: 256px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #dadce0;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.cliente-item {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.cliente-item:hover {
  background: #f8fafd;
}

.cliente-item.highlighted {
  background: #e8f0fe;
}

.cliente-nome {
  font-size: 13px;
  font-weight: 500;
  color: #202124;
}

.cliente-id {
  font-size: 11px;
  color: #70757a;
}

.cliente-empty {
  padding: 12px;
  font-size: 13px;
  color: #70757a;
}
</style>
